import axios from 'axios';
import properties from '../../../../conf/properties';
import QueryBuilder from '../../mts-client/QueryBuilder';

const rckmsRsEndpoint = `${properties.rsBaseServiceUri}${properties.rsCrudContextPath}/api/resources`.format('rckms');
const cdsRsEndpoint = `${properties.rsBaseServiceUri}${properties.rsCrudContextPath}/api/resources`.format('cds');
const coreRsEndpoint = `${properties.rsBaseServiceUri}${properties.rsCrudContextPath}/api/resources`.format('core');
const serviceMap = {
    rckms: rckmsRsEndpoint,
    cds: cdsRsEndpoint,
    core: coreRsEndpoint
};

/**
 * This dynamically builds a client object from a data model crudConfig file and exposes available crud functions. Each available function is exposed as an async Promise based function via the execute factory function.
 *
 *
 * @param crudConfig
 * @returns {string}
 * @constructor
 */
export const apiClient = (crudConfig) => {
    const execute = (functionName, sessionToken, ...args) => {
        if (typeof crudConfig.functions[functionName] != 'object') {
            return Promise.reject('Requested function not available. Check CRUD config.');
        }

        const functionConfig = crudConfig.functions[functionName];

        /**
         * Builds general GET method async request
         * @param functionConfig
         * @param args
         * @returns {Promise<any>}
         */
        const get = (functionConfig, args = null) => {
            return new Promise((resolve, reject) => {
                let uri = `${serviceMap[functionConfig.service]}/${crudConfig.endpoint}${
                    args?.[0] ? `/${args[0]}` : ''
                }`;

                let query = QueryBuilder({
                    ...functionConfig.query,
                    sessionId: sessionToken
                });
                if (functionConfig.args != null && functionConfig.args.length > 0) {
                    for (let a in functionConfig.args) {
                        query = query.replace();
                    }
                }

                uri += query;

                axios
                    .get(uri)
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((err) => {
                        console.log(err);
                        reject(err);
                    });
            });
        };

        /**
         * Builds general PUT method async request
         * @param functionConfig
         * @param args [primaryKey, updatedDoc]
         * @returns {Promise<any>}
         */
        const put = (functionConfig, args) => {
            return new Promise((resolve, reject) => {
                let uri = `${serviceMap[functionConfig.service]}/${crudConfig.endpoint}/${args[0]}`;

                //TODO: Maybe process arguments in query parameters
                // let body = {};
                // if (functionConfig.args != null && functionConfig.args.length > 0) {
                //     for (let i in functionConfig.args) {
                //         if (args[i] != undefined) {
                //             body[functionConfig.args[i]] = args[i];
                //         } else {
                //             break;
                //         }
                //     }
                // }

                let query = QueryBuilder({
                    ...functionConfig.query,
                    sessionId: sessionToken,
                    property: `RS_RETURN_RESOURCE=true`
                });
                uri += query;
                axios
                    .put(uri, args[1])
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((err) => {
                        console.log(err);
                        reject(err);
                    });
            });
        };

        /**
         * Builds general POST method async request
         * @param functionConfig
         * @param args
         * @returns {Promise<any>}
         */
        const post = (functionConfig, args) => {
            return new Promise((resolve, reject) => {
                let uri = `${serviceMap[functionConfig.service]}/${crudConfig.endpoint}`;

                //TODO: Maybe process arguments in query parameters
                // let body = {};
                // if (functionConfig.args != null && functionConfig.args.length > 0) {
                //     for (let i in functionConfig.args) {
                //         if (args[i] != undefined) {
                //             body[functionConfig.args[i]] = args[i];
                //         } else {
                //             break;
                //         }
                //     }
                // }

                let query = QueryBuilder({
                    ...functionConfig.query,
                    sessionId: sessionToken,
                    property: `RS_RETURN_RESOURCE=true`
                });
                uri += query;
                axios
                    .post(uri, args[0])
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((err) => {
                        reject(err);
                    });
            });
        };

        /**
         * Builds general DELETE method async request
         * @param functionConfig
         * @param args
         * @returns {Promise<any>}
         */
        const del = (functionConfig, args) => {
            return new Promise((resolve, reject) => {
                let primaryKey = args[0];
                let uri = `${serviceMap[functionConfig.service]}/${crudConfig.endpoint}/${primaryKey}`;

                //TODO: Maybe process arguments in query parameters
                // let body = {};
                // if (functionConfig.args != null && functionConfig.args.length > 0) {
                //     for (let i in functionConfig.args) {
                //         if (args[i] != undefined) {
                //             body[functionConfig.args[i]] = args[i];
                //         } else {
                //             break;
                //         }
                //     }
                // }

                let query = QueryBuilder({
                    ...functionConfig.query,
                    sessionId: sessionToken,
                    property: `RS_RETURN_RESOURCE=true`
                });
                uri += query;
                axios
                    .delete(uri)
                    .then((response) => {
                        // Resolving the deleted doc's primary key since no doc comes back
                        resolve(primaryKey);
                    })
                    .catch((err) => {
                        console.log(err);
                        reject(err);
                    });
            });
        };

        switch (functionConfig.method) {
            case 'get':
                return get(functionConfig, args);
            case 'post':
                return post(functionConfig, args);
            case 'put':
                return put(functionConfig, args);
            case 'delete':
                return del(functionConfig, args);
            default:
                return Promise.reject('Method not supported');
        }
    };

    // Client built from crud config "functions" property. If this is undefined, no functions are exposed.
    const buildClient = (crudConfig) => {
        return Object.keys(crudConfig.functions).reduce((map, v, i) => {
            map[v] = (...args) => {
                return execute(v, ...args);
            };
            return map;
        }, {});
    };

    return buildClient(crudConfig);
};

export default apiClient;
